<template>
  <v-container fluid>
    <loading-overlay :loading="loading" />
    <div style="width: 100%;">
      <div class="scroll-container">
        <custom-toolbar
          :title="toolbarTitle"
          :search="search"
          :selectedColumn="selectedColumn"
          :columnOptions="columnOptions"
          :exportToExcel="exportToExcel"
          :items="items"
          :categories="categories"
          :providers="providers"
          showSelect
          showSearch
          @update:search="search = $event"
          @update:selectedColumn="selectedColumn = $event"
          @update:selectedCategory="selectedCategory = $event"
          @update:selectedProvider="selectedProvider = $event"
        />
        <div class="row-count">
          Total Items: <b>{{ rowCount.total }}</b> 
          <template v-if="$route.name === 'ODK'">
            | KOCOWA Items: <b>{{ rowCount.kocowa }}</b>
            | Non-KOCOWA Items: <b>{{ rowCount.nonKocowa }}</b>
          </template>
          <template v-if="$route.name === 'ODK'">
            | Total Kocowa Duration: <b>{{ totalKocowaDuration }}</b> hours
          </template>
          <template v-else>
            | Total Media Duration: <b>{{ totalMediaDuration }}</b> hours
          </template>
        </div>
        <div ref="dummyScroll" id="scroll" style="overflow-x: auto !important;" @scroll="syncScrolls">
          <div id="dummyTable" ref="dummyTable" style="height:1px;"></div>
        </div>
        <v-data-table
          ref="dataTable"
          :headers="headers"
          :items="filteredItems"
          class="elevation-0 no-card-style wide-table"
          :items-per-page="itemsPerPage"
          :hide-default-footer="true"
          :sort-by="'season_on_air_date'"
          :sort-desc="true"
          fixed-header
          height="1800px"
        />
      </div> 
    </div>
  </v-container>
</template>

  
<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import * as XLSX from 'xlsx';
import LoadingOverlay from './LoadingOverlayPartner.vue';
import CustomToolbar from './CustomToolbar.vue';

export default {
  name: 'ContentList',
  components: {
    LoadingOverlay,
    CustomToolbar
  },
  data() {
    return {
      services: {
          ANDTV: { headers: this.getAndtvHeaders(), apiService: 'andtv', fileName: 'data_andtv.xlsx' },
          VIKI: { headers: this.getVikiHeaders(), apiService: 'viki', fileName: 'data_viki.xlsx' },
          ROKU: { headers: this.getRokuHeaders(), apiService: 'roku', fileName: 'data_roku.xlsx' },
          ODK: { headers: this.getOdkHeaders(), apiService: 'odk', fileName: 'data_odk.xlsx' },
          COMCAST: { headers: this.getRokuHeaders(), apiService: 'comcast', fileName: 'data_comcast.xlsx' },
          AMAZON: { headers: this.getRokuHeaders(), apiService: 'amazon', fileName: 'data_amazon.xlsx' }
      },
      service: null,
      items: [],
      search: '',
      selectedColumn: 'season_id',
      itemsPerPage: 5000,
      loading: false,
      categories: [], // Define categories here
      providers: [] // Define providers here
    };
  },
  computed: {
    ...mapGetters(['userEmail', 'token', 'auth']),
    filteredItems() {
      const search = this.search.toLowerCase();
      if (!this.selectedColumn) return this.items;

      return this.items.filter(item =>
        String(item[this.selectedColumn] || '').toLowerCase().includes(search)
      )
      .map(item => ({
        ...item,
        service_type: item.service_type === 'kocowa' 
          ? 'KOCOWA' 
          : (item.service_type === 'none' ? 'Non KOCOWA' : item.service_type)
      }));
    },
    toolbarTitle() {
      if (this.$route.name === 'ANDTV') {
        return '&TV';
      } else if (this.$route.name === 'ODK') {
        return 'ODK';
      } else if (this.$route.name === 'ROKU') {
        return 'Roku Channel';
      } else if (this.$route.name === 'AMAZON') {
        return 'Prime Video';
      }
      else {
        return this.$route.name.charAt(0).toUpperCase() + this.$route.name.slice(1).toLowerCase();
      }
    },
    headers() {
      return this.service ? this.services[this.service].headers : [];
    },
    columnOptions() {
      return this.headers.map(header => ({ text: header.text, value: header.value }));
    },
    rowCount() {
      const totalItems = this.filteredItems.length;

      if (this.$route.name === 'ODK') {
        const kocowaItems = this.filteredItems.filter(item => item.service_type === 'KOCOWA').length;
        const nonKocowaItems = totalItems - kocowaItems;

        return {
          total: totalItems.toLocaleString(),
          kocowa: kocowaItems.toLocaleString(),
          nonKocowa: nonKocowaItems.toLocaleString()
        };
      }

      return {
        total: totalItems.toLocaleString()
      };
    },
    totalMediaDuration() {
      const total = this.filteredItems.reduce((total, item) => {
        return total + (parseFloat(item.media_duration) || 0);
      }, 0);

      return parseFloat(total.toFixed(2)).toLocaleString();
    },
    totalKocowaDuration() {
      const total = this.filteredItems
        .filter(item => item.service_type === 'KOCOWA')
        .reduce((total, item) => {
          return total + (parseFloat(item.media_duration) || 0); 
        }, 0);

      return parseFloat(total.toFixed(2)).toLocaleString();
    }
  },
  methods: {
    ...mapActions(['fetchUserData']),
    getAndtvHeaders() {
      return [
          { text: 'Season ID', value: 'season_id' },
          { text: 'Provider', value: 'provider_name' },
          { text: 'English Title', value: 'season_name' },
          { text: 'Korean Title', value: 'season_name_kr' },
          { text: 'Publish', value: 'season_on_air_date', width: '120px' },
          { text: 'Premiere', value: 'premiere', width: '120px' },
          { text: 'Finale', value: 'last_episode_on_air_date', width: '120px' },
          { text: '# of pip active', value: 'num_of_pip_active' },
          { text: 'Category', value: 'category_name' },
          { text: 'Media Duration (hour)', value: 'media_duration' }
      ];
    },
    getVikiHeaders() {
      return [
          { text: 'Season ID', value: 'season_id' },
          { text: 'Provider', value: 'provider_name' },
          { text: 'English Title', value: 'season_name' },
          { text: 'Korean Title', value: 'season_name_kr' },
          { text: 'Publish', value: 'season_on_air_date', width: '120px' },
          { text: 'Premiere', value: 'premiere', width: '120px' },
          { text: 'Finale', value: 'last_episode_on_air_date', width: '120px' },
          { text: '# of pip active', value: 'num_of_pip_active' },
          { text: 'Category', value: 'category_name' },
          { text: 'Media Duration (hour)', value: 'media_duration' },
          { text: 'Deploy Subtitles', value: 'deploy_sub' }
      ];
    },
    getRokuHeaders() {
      return [
          { text: 'Season ID', value: 'season_id' },
          { text: 'Provider', value: 'provider_name' },
          { text: 'English Title', value: 'season_name' },
          { text: 'Korean Title', value: 'season_name_kr' },
          { text: 'Publish', value: 'season_on_air_date', width: '120px' },
          { text: 'Premiere', value: 'premiere', width: '120px' },
          { text: 'Finale', value: 'last_episode_on_air_date', width: '120px' },
          { text: '# of pip active', value: 'num_of_pip_active' },
          { text: 'Category', value: 'category_name' },
          { text: 'Media Duration (hour)', value: 'media_duration' }
      ];
    },
    getOdkHeaders() {
      return [
          { text: 'Season ID', value: 'season_id' },
          { text: 'Provider', value: 'provider_name' },
          { text: 'English Title', value: 'season_name' },
          { text: 'Korean Title', value: 'season_name_kr' },
          { text: 'Publish', value: 'season_on_air_date', width: '120px' },
          { text: 'Premiere', value: 'premiere', width: '120px' },
          { text: 'Finale', value: 'last_episode_on_air_date', width: '120px' },
          { text: '# of pip active', value: 'num_of_pip_active' },
          { text: 'Category', value: 'category_name' },
          { text: 'Media Duration (hour)', value: 'media_duration' },
          { text: 'Service Type', value: 'service_type' }
      ];
    },
    fetchData() {
      this.loading = true;

      if (!this.userEmail || !this.token) {
        console.error('User email or token is missing');
        this.loading = false;
        return;
      }

      const url = `https://pcld-api.wavveanalytics.com/API/contents-list?user_email=${encodeURIComponent(this.userEmail)}&token=${encodeURIComponent(this.token)}`;

      axios.get(url)
        .then(response => {
          const serviceType = this.services[this.service]?.apiService || '';
          if (response.data.service === serviceType) {
            this.items = response.data.content_list || [];
            this.categories = [...new Set(this.items.map(item => item.category_name))];
            this.providers = [...new Set(this.items.map(item => item.provider_name))];
          } else {
            this.items = [];
            this.categories = [];
            this.providers = [];
          }
        })
        .catch(error => {
          console.error('Error fetching data:', error);
          this.items = [];
          this.categories = [];
          this.providers = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportToExcel() {
      const headers = this.headers.map(h => h.text);
      const data = this.filteredItems.map(item =>
        this.headers.map(h => item[h.value]) // 필드값 대신 헤더 순서를 따름
      );

      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      const fileName = this.services[this.service]?.fileName || 'data.xlsx';
      XLSX.writeFile(wb, fileName);
    },
    syncScrolls(event) {
      const dataTableWrapper = document.querySelector('.v-data-table__wrapper');
      const dummyScroll = this.$refs.dummyScroll;
      const dummyTable = this.$refs.dummyTable;
      const dataTable = this.$refs.dataTable;

      if (dummyTable && dataTable) {
        if (dataTableWrapper) {
          if (event.target.id === "scroll") {
            dataTableWrapper.scrollLeft = dummyScroll.scrollLeft; 
          } else {
            console.log('Scrolling data table');
            dummyScroll.scrollLeft = dataTableWrapper.scrollLeft;
          }
        } else {
          console.error('Data table wrapper is undefined');
        }
      } else {
        console.error('One or both elements are undefined');
      }
    },
    syncScrollWidth() {
      const dataTableWrapper = document.querySelector('.v-data-table__wrapper > table');
      const dummyTable = document.getElementById('dummyTable');
      dummyTable.style.width = dataTableWrapper.offsetWidth + "px";
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.syncScrollWidth();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.syncScrollWidth();
    });
  },
  beforeUnmount() {
    const dummyScroll = this.$refs.dummyScroll;
    const dataTable = this.$refs.dataTable;

    if (dummyScroll && dataTable) {
      const dataTableWrapper = dataTable.$el?.querySelector('.v-data-table__wrapper');
      if (dataTableWrapper) {
        dummyScroll.removeEventListener('scroll', this.syncScrolls);
        dataTableWrapper.removeEventListener('scroll', this.syncScrolls);
        console.log('Event listeners removed');
      } else {
        console.error('Data table wrapper is undefined');
      }
    } else {
      console.error('One or both elements are undefined');
    }
  },
  created() {
    this.service = this.$route.name.toUpperCase();
    this.fetchData();
  }
};
</script>
  
<style scoped>

.scroll-container {
  position: relative;
  margin: 15px;
}

.v-data-table__wrapper {
  overflow-x: auto !important;
}


.wide-table {
  width: 100% !important; /* Ensure the table takes full width */
  overflow-x: hidden !important; /* Remove horizontal scroll */
}

.v-data-table__wrapper {
  overflow-x: hidden !important; /* Remove horizontal scroll */
  display: block; /* Set block element to occupy full width */
  width: 100% !important; /* Set block element width to 100% */
}

.toolbar-container {
  margin-bottom: 50px;
  padding-top: 20px;
}

::v-deep .v-data-table-header th {
  background-color: #ededed !important;
  white-space: nowrap; /* Prevent header text wrapping */
  color: #636363 !important;
}

::v-deep .v-data-table-header th:hover {
  color: black !important;
}

::v-deep .v-data-table-header th > .v-data-table-header__content {
  color: white;
}

::v-deep .v-data-table tbody tr:hover {
  background-color: #e5eef9 !important;
}

.row-count {
  padding: 7px 0;
  font-size: 12px;
  color: #636363;
}

.v-data-table {
  table-layout: auto !important;
  width: 100% !important;
}

::v-deep .v-data-table > table {
  width: auto !important;
}

::v-deep .v-data-table > table > thead > tr > th {
  white-space: nowrap; /* Prevent header text wrapping */
}

::v-deep .v-data-table > table > tbody > tr > td {
  white-space: nowrap; /* Prevent cell text wrapping */
}

::v-deep .v-data-table > div {
  display: block; /* Set block element to occupy full width */
  width: 100% !important; /* Set block element width to 100% */
}

.v-data-table>.v-data-table__wrapper>table {
    border-spacing: 0;
}

</style>
